import React from 'react';

export type SearchResultsFallbackContentState = 'notSearchResults' | 'hidden' | 'visible';

interface SearchResultsFallbackContentContextValue {
  fallbackContentState: SearchResultsFallbackContentState;
  setFallbackContentState: (state: SearchResultsFallbackContentState) => void;
}

const SearchResultsFallbackContentContext = React.createContext<
  SearchResultsFallbackContentContextValue | undefined
>(undefined);

let fallbackContentState: SearchResultsFallbackContentState = 'notSearchResults';
const fallbackContentStateListeners = new Set<() => void>();

const subscribeToFallbackContentState = (listener: () => void) => {
  fallbackContentStateListeners.add(listener);

  return () => fallbackContentStateListeners.delete(listener);
};

const getFallbackContentStateSnapshot = () => fallbackContentState;

export const setSearchResultsFallbackContentState = (
  nextFallbackContentState: SearchResultsFallbackContentState
) => {
  if (fallbackContentState === nextFallbackContentState) {
    return;
  }

  fallbackContentState = nextFallbackContentState;
  fallbackContentStateListeners.forEach((listener) => listener());
};

export const resetSearchResultsFallbackContentState = () => {
  setSearchResultsFallbackContentState('notSearchResults');
};

export const SearchResultsFallbackContentProvider = ({
  children,
  initialFallbackContentState = 'notSearchResults',
}: {
  children: React.ReactNode;
  initialFallbackContentState?: SearchResultsFallbackContentState;
}) => {
  const [fallbackContentState, setFallbackContentState] =
    React.useState<SearchResultsFallbackContentState>(initialFallbackContentState);

  return (
    <SearchResultsFallbackContentContext.Provider
      value={{ fallbackContentState, setFallbackContentState }}
    >
      {children}
    </SearchResultsFallbackContentContext.Provider>
  );
};

export const useSearchResultsFallbackContent = () => {
  const context = React.useContext(SearchResultsFallbackContentContext);
  const fallbackContentState = React.useSyncExternalStore(
    subscribeToFallbackContentState,
    getFallbackContentStateSnapshot,
    getFallbackContentStateSnapshot
  );

  return (
    context ?? {
      fallbackContentState,
      setFallbackContentState: setSearchResultsFallbackContentState,
    }
  );
};
